import { API_ROUTES, postEncryptedJson, postEncryptedFormData, updateProfile, updateProfileStatus } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

function buildDocImageUrl(filePath) {
  if (!filePath) return filePath;
  return filePath;
}

const UserDocumentType = {
  PROOF_OF_IDENTITY: 0,
  PROOF_OF_ADDRESS: 1,
  PROFILE: 2,
  SELFIE: 3,
  OTHER: 4,
  PROOF_OF_SIGNATURE: 8,
  PROOF_OF_VIDEO_SIGNATURE: 14,
};

const UserDocumentSubType = {
  OTHER: 0,
  ID_CARD: 1,
  PASSPORT: 2,
  CARD_OF_RESIDENCE: 3,
  PALESTINIAN_CARD: 4,
  VISA: 5,
  SELFIE: 7,
  UTILITY_BILL: 10,
  PROFILE: 11,
  CERTIFICATE_OF_RESIDENCE: 12,
  SIGNATURE: 13,
  CHECK_SIGNATURE: 15,
  SIGNATURE_VIDEO: 16,
};

const DocumentFileName = {
  FRONT_IMAGE: "FRONT_ID_IMAGE",
  BACK_IMAGE: "BACK_ID_IMAGE",
  ADDRESS_IMAGE: "ADDRESS_IMAGE",
  SELFIE: "SELFIE_IMAGE",
  PROFILE: "PROFILE_IMAGE",
  OTHER: "OTHER",
  SIGNATURE_IMAGE: "SIGNATURE_IMAGE",
  SIGNATURE_VIDEO: "SIGNATURE_VIDEO",
  COMPANY_IMAGE: "COMPANY_IMAGE",
};

/**
 * Server-side risk scoring can throw (e.g. missing BusinessKey::RESTRICTED_COUNTRIES)
 * AFTER documents/agreements were already saved. Treat those as non-blocking success.
 */
function isBenignServerSideNoise(message) {
  const msg = String(message || "");
  if (!msg) return false;
  return (
    /RESTRICTED_COUNTRIES/i.test(msg) ||
    /Undefined constant\s+App\\Enums\\BusinessKey/i.test(msg) ||
    /Undefined constant.*BusinessKey/i.test(msg) ||
    // Logging helper crashes after regulation/profile already saved
    /Call to a member function description\(\) on null/i.test(msg)
  );
}

/**
 * Extract success/failure from encrypted API responses.
 * Saves often succeed on the server while the client fails to decrypt MESSAGES,
 * leaving CODE undefined — treat HTTP 200 without a known failure CODE as OK.
 */
function resolveApiOutcome(response) {
  const httpStatus = response?.status;
  let result = response?.decryptedData ?? response?.data ?? null;

  if (typeof result === "string") {
    // Plain PHP error pages sometimes come back as HTML/text.
    if (isBenignServerSideNoise(result)) {
      return {
        result: { MESSAGE: result },
        code: AppConstants.responseOk,
        httpStatus: httpStatus || 200,
        stillEncrypted: false,
        isSuccess: true,
      };
    }
    try {
      result = JSON.parse(result);
    } catch (_) {
      const m = result.match(/"CODE"\s*:\s*(\d+)/);
      result = m ? { CODE: parseInt(m[1], 10) } : { MESSAGE: result };
    }
  }

  const stillEncrypted =
    result &&
    typeof result === "object" &&
    Array.isArray(result.MESSAGES) &&
    result.CODE == null &&
    result.code == null;

  let code =
    result?.CODE ??
    result?.code ??
    result?.body?.CODE ??
    result?.body?.code ??
    result?.data?.CODE ??
    result?.data?.code ??
    result?.MESSAGES?.CODE ??
    result?.MESSAGES?.code ??
    null;

  if (code != null) {
    const n = typeof code === "number" ? code : parseInt(String(code), 10);
    code = Number.isFinite(n) ? n : null;
  }

  const message =
    result?.MESSAGE ??
    result?.message ??
    result?.error ??
    result?.ERROR ??
    "";

  // Documents/agreements already persisted; ignore known post-save risk-score noise.
  if (isBenignServerSideNoise(message) || isBenignServerSideNoise(JSON.stringify(result || {}))) {
    return {
      result: result && typeof result === "object" ? result : {},
      code: AppConstants.responseOk,
      httpStatus: httpStatus || 200,
      stillEncrypted: Boolean(stillEncrypted),
      isSuccess: true,
    };
  }

  const FAILURE_CODES = new Set([
    AppConstants.responseFailed,
    AppConstants.responseNotFound,
    AppConstants.messagesErrorOTP,
    AppConstants.messagesErrorSendingSMS,
    AppConstants.invalidSecretKey,
    AppConstants.catchError,
    AppConstants.invalidInputs,
    AppConstants.loginBlocked,
  ]);

  const isSuccess =
    code === AppConstants.responseOk ||
    code === 200 ||
    code === AppConstants.messagesOK ||
    (httpStatus === 200 && stillEncrypted) ||
    (httpStatus === 200 && code == null && !FAILURE_CODES.has(code));

  return {
    result: result && typeof result === "object" ? result : {},
    code,
    httpStatus,
    stillEncrypted: Boolean(stillEncrypted),
    isSuccess,
  };
}

const KycStatus = {
  UPDATED_KYC: -3,
  HAS_KYC_ERROR: -2,
  HAS_APPROVAL_ERROR: -1,
  PENDING: 0,
  APPROVED: 1,
  VERIFIED: 2,
  VERIFIED_NOT_CHECKED: 3,
};

export { UserDocumentType, UserDocumentSubType, DocumentFileName, KycStatus };

class ProfileInfoController {
  static profileInfoCache = new Map();
  static profileInfoInFlight = new Map();
  static PROFILE_INFO_CACHE_TTL_MS = 10 * 60 * 1000;

  /**
   * Invalidate the cached profile so the next getProfileInfo() hits the server.
   * Call after any write (update profile, save regulation, submit documents).
   */
  static clearProfileCache() {
    ProfileInfoController.profileInfoCache.clear();
    ProfileInfoController.profileInfoInFlight.clear();
  }

  /**
   * Fetch full profile data (with documents).
   * WITH_DOC=0 makes the backend eager-load user_information, user_document AND
   * user_regulation/company/entity (matches the Flutter client). WITH_DOC=1 omits
   * user_regulation, which leaves financial/regulation data empty.
   */
  static async getProfileInfo({ withDoc = 0, forceRefresh = false } = {}) {
    const cacheKey = `withDoc:${withDoc}`;
    const now = Date.now();

    if (!forceRefresh) {
      const cached = ProfileInfoController.profileInfoCache.get(cacheKey);
      if (
        cached &&
        cached.timestamp &&
        now - cached.timestamp < ProfileInfoController.PROFILE_INFO_CACHE_TTL_MS
      ) {
        return cached.value;
      }

      const inFlight = ProfileInfoController.profileInfoInFlight.get(cacheKey);
      if (inFlight) {
        return await inFlight;
      }
    }

    try {
      const requestPromise = (async () => {
        const response = await postEncryptedJson(API_ROUTES.GET_PROFILE, {
          WITH_DOC: withDoc,
        });
        const data = response?.decryptedData || response?.data;

        if (!data || typeof data !== "object") {
          return { success: false, error: "Invalid response format" };
        }

        if (data.CODE === AppConstants.responseOk && data.DATA) {
          const raw = data.DATA;
          const info = raw.user_information || raw.USER_INFORMATION || raw.userInformation || {};
          const reg = raw.user_regulation || raw.USER_REGULATION || raw.userRegulation || {};

          const result = {
            success: true,
            data: {
              id: raw.ID || raw.id,
              clientId: raw.CLIENT_ID || raw.clientId || "",
              userName: raw.USER_NAME || raw.userName || raw.EMAIL || "",
              nickName: raw.NICK_NAME || raw.nickName || "",
              type: raw.TYPE ?? raw.type,
              internalType: info.INTERNAL_TYPE ?? info.internalType ?? info.internal_type,
              firstName: info.F_NAME || info.FIRST_NAME || info.firstName || "",
              fatherName: info.FATHER_NAME || info.fatherName || "",
              lastName: info.L_NAME || info.LAST_NAME || info.lastName || "",
              motherName: info.MOTHER_NAME || info.motherName || "",
              gender: info.GENDER ?? info.gender,
              profession: info.PROFESSION || info.profession || "",
              marital: info.MARITAL ?? info.marital,
              spouseName: info.SPOUSE_NAME || info.spouseName || "",
              spouseProfession: info.SPOUSE_PROFESSION || info.spouseProfession || "",
              isPep: info.IS_PEP ?? info.isPep ?? false,
              birthDay: info.BIRTH_DAY || info.birthDay || "",
              placeOfBirth: info.PLACE_OF_BIRTH || info.placeOfBirth || "",
              state: info.STATE || info.state || "",
              city: info.CITY || info.city || "",
              zipCode: info.ZIP_CODE || info.zipCode || "",
              address: info.ADDRESS || info.address || "",
              workAddress: info.WORK_ADDRESS || info.workAddress || "",
              country: info.COUNTRY || info.country || "",
              nationality: info.NATIONALITY || info.nationality || "",
              residence: info.RESIDENCE ?? info.residence,
              beneficialRight: info.BENEFICAL_RIGHT ?? info.beneficalRight,
              identificationNumber: info.IDENTIFICATION_NUMBER || info.identificationNumber || "",
              email: info.EMAIL || info.email || raw.EMAIL || raw.USER_NAME || "",
              taxId: reg.TAX_ID || reg.taxId || "",
              netWorth: reg.NET_WORTH ?? reg.netWorth,
              annualIncome: reg.ANNUAL_INCOME ?? reg.annualIncome,
              annualDeposit: reg.ANNUAL_DEPOSIT ?? reg.annualDeposit,
              employmentIndustry: reg.EMPLOYMENT_INDUSTRY ?? reg.employmentIndustry,
              educationLevel: reg.EDUCATION_LEVEL ?? reg.educationLevel,
              sourceOfWealth: reg.SOURCE_OF_WEALTH ?? reg.sourceOfWealth,
              employmentStatus: reg.EMPLOYMENT_STATUS ?? reg.employmentStatus,
              experienceForex: reg.EXPERIENCE_FOREX ?? reg.experienceForex,
              experienceStocks: reg.EXPERIENCE_STOCKS ?? reg.experienceStocks,
              experienceFutures: reg.EXPERIENCE_FUTURES ?? reg.experienceFutures,
              experienceCfd: reg.EXPERIENCE_CFD ?? reg.experienceCfd,
              opsPace: reg.OPS_PACE ?? reg.opsPace ?? null,
              avgTxAmount: reg.AVG_TX_AMOUNT ?? reg.avgTxAmount ?? null,
              expectedAnnualActivity:
                reg.EXPECTED_ANNUAL_ACTIVITY ?? reg.expectedAnnualActivity ?? null,
              raw,
            },
          };

          ProfileInfoController.profileInfoCache.set(cacheKey, {
            timestamp: Date.now(),
            value: result,
          });
          return result;
        }

        if (data.CODE === 401) {
          return { success: false, error: "Authentication failed. Please login again." };
        }

        return {
          success: false,
          error: data.MESSAGE || `Error: CODE ${data.CODE || "Unknown"}`,
        };
      })();

      if (!forceRefresh) {
        ProfileInfoController.profileInfoInFlight.set(cacheKey, requestPromise);
        try {
          return await requestPromise;
        } finally {
          ProfileInfoController.profileInfoInFlight.delete(cacheKey);
        }
      }

      return await requestPromise;
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load profile information",
      };
    }
  }

  /**
   * Save profile data per step.
   * Matches Flutter _updateUserInformations() which sends step-specific data:
   *   'client'   → USER_ID, IS_CREATE, USER_NICK_NAME, USER_TYPE
   *   'personal' → USER_ID, IS_CREATE, USER_INFORMATION
   *   'address'  → USER_ID, IS_CREATE, USER_INFORMATION
   */
  static async saveProfile({ userId, nickName, userType, userInformation, updateType }) {
    try {
      const body = {
        USER_ID: userId,
        IS_CREATE: 1,
      };

      if (updateType === "client") {
        body.USER_NICK_NAME = nickName;
        body.USER_TYPE = userType;
      } else {
        body.USER_INFORMATION = userInformation;
      }

      const response = await updateProfile(body);
      const { result, code, isSuccess } = resolveApiOutcome(response);

      if (isSuccess) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || result?.message || `Server returned CODE ${code}`,
        code,
      };
    } catch (error) {
      console.error("[ProfileInfoController] saveProfile error:", error);
      return { success: false, error: error.message || "Failed to save profile" };
    }
  }

  /**
   * Save regulation data (Step 4).
   * Uses postEncryptedJson (JSON POST) to match Flutter's DatabaseApi.callServer
   * which uses postData (not multipart) when no images are being uploaded.
   * Must include USER_DOCUMENTS and related fields to match Flutter's full request.
   */
  static async saveRegulation({
    userId,
    userInformation,
    userRegulation,
    userDocuments,
    selectedDocSubTypes,
  }) {
    try {
      const body = {
        USER_ID: userId,
        USER_INFORMATION: userInformation,
        USER_REGULATION: userRegulation,
        USER_DOCUMENTS: userDocuments || [],
        DOCUMENTS_TO_DELETE: {},
        DOCUMENTS_TO_UPDATE: {},
        SELECTED_DOCUMENTS_SUBTYPES: selectedDocSubTypes || {},
      };

      const response = await postEncryptedJson(
        API_ROUTES.UPDATE_PERSONAL_INFORMATION,
        body
      );
      const { result, code, isSuccess } = resolveApiOutcome(response);

      if (isSuccess) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || result?.message || `Server returned CODE ${code}`,
        code,
      };
    } catch (error) {
      console.error("[ProfileInfoController] saveRegulation error:", error);
      if (
        isBenignServerSideNoise(error?.message) ||
        isBenignServerSideNoise(error?.response?.data)
      ) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return { success: false, error: error.message || "Failed to save regulation" };
    }
  }

  /**
   * Fetch agreements from API.
   * Mirrors Flutter _loadAgreements() calling GET_AGREEMENTS.
   */
  static async getAgreements() {
    try {
      const response = await postEncryptedJson(API_ROUTES.GET_AGREEMENTS, {});
      const data = response?.decryptedData || response?.data;

      if (data?.CODE === AppConstants.responseOk && Array.isArray(data.DATA)) {
        return { success: true, data: data.DATA };
      }
      return { success: true, data: [] };
    } catch (error) {
      console.error("[ProfileInfoController] getAgreements error:", error);
      return { success: false, data: [], error: error.message };
    }
  }

  /**
   * Submit documents and agreement signatures (Step 5).
   * Mirrors Flutter _insertUserDocuments() calling CREATE_USER_DOCUMENTS.
   */
  static async submitDocuments({ email, identityDocType, addressDocType, imageFiles, agreementSignatures }) {
    try {
      const hasSignatureVideo = !!(imageFiles && imageFiles.SIGNATURE_VIDEO);
      const userDocuments = {
        SELFIE_IMAGE: UserDocumentSubType.SELFIE,
        FRONT_ID_IMAGE: identityDocType ?? null,
        BACK_ID_IMAGE: identityDocType ?? "",
        ADDRESS_IMAGE: addressDocType ?? null,
        SHAREHOLDER_DOCUMENT_TYPE: null,
        DIRECTOR_DOCUMENT_TYPE: null,
        BENIFICAL_DOCUMENT_TYPE: UserDocumentSubType.ID_CARD,
        SIGNATURE_DOCUMENT_TYPE: UserDocumentSubType.SIGNATURE,
        ...(hasSignatureVideo ? { SIGNATURE_VIDEO_TYPE: UserDocumentSubType.SIGNATURE_VIDEO } : {}),
        SIGNATURE_CHECK_TYPE: UserDocumentSubType.CHECK_SIGNATURE,
      };

      const data = {
        EMAIL: email,
        IS_CREATE: 1,
        USER_DOCUMENTS: userDocuments,
      };

      // Add agreement ID mappings (AGREEMENT_{ID}_ID = id)
      if (agreementSignatures) {
        for (const [agreementId] of Object.entries(agreementSignatures)) {
          data[`AGREEMENT_${agreementId}_ID`] = String(agreementId);
        }
      }

      // Build imageFiles map: document files + agreement signature images
      const allFiles = { ...imageFiles };
      if (agreementSignatures) {
        for (const [agreementId, file] of Object.entries(agreementSignatures)) {
          if (file) {
            allFiles[`AGREEMENT_${agreementId}_SIGNATURE`] = file;
          }
        }
        const firstSignature = Object.values(agreementSignatures).find(Boolean);
        if (firstSignature && !allFiles.SIGNATURE_AGREEMENT_IMAGE) {
          allFiles.SIGNATURE_AGREEMENT_IMAGE = firstSignature;
        }
      }

      const response = await postEncryptedFormData(
        API_ROUTES.CREATE_USER_DOCUMENTS_MT5_ACCOUNT,
        data,
        allFiles
      );
      const { result, code, isSuccess } = resolveApiOutcome(response);

      if (isSuccess) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      if (code === AppConstants.responseFailed) {
        return {
          success: false,
          error:
            result?.MESSAGE ||
            result?.message ||
            "Your documents may already be saved. Refresh the page or contact support if this continues.",
          code,
        };
      }
      return {
        success: false,
        error: result?.MESSAGE || result?.message || `Server returned CODE ${code}`,
        code,
      };
    } catch (error) {
      console.error("[ProfileInfoController] submitDocuments error:", error);
      // Axios may surface the PHP enum error as a thrown message after save.
      if (
        isBenignServerSideNoise(error?.message) ||
        isBenignServerSideNoise(error?.response?.data)
      ) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return { success: false, error: error.message || "Failed to submit documents" };
    }
  }

  /**
   * Update user profile (edit mode) — mirrors Flutter _updateUserInformations().
   * Sends user info + documents + images to UPDATE_PERSONAL_PROFILE via FormData.
   */
  static async updateUserProfile({
    userId,
    userInformation,
    nickName,
    userType,
    userDocuments: existingDocs,
    selectedDocSubTypes,
    imageFiles,
    imagesToUpdate,
    imagesToDelete,
  }) {
    try {
      const data = {
        USER_ID: userId,
        USER_DOCUMENTS: ProfileInfoController.normalizeDocumentsForUpdate(existingDocs),
        DOCUMENTS_TO_DELETE: imagesToDelete || {},
        DOCUMENTS_TO_UPDATE: imagesToUpdate || {},
        USER_INFORMATION: userInformation,
        USER_NICK_NAME: nickName,
        USER_TYPE: userType,
        SELECTED_DOCUMENTS_SUBTYPES: selectedDocSubTypes || {},
      };

      const response = await postEncryptedFormData(
        API_ROUTES.UPDATE_PERSONAL_PROFILE,
        data,
        imageFiles || {}
      );
      const { result, code, isSuccess, httpStatus } = resolveApiOutcome(response);

      if (isSuccess) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }

      if (!result || (typeof result === "object" && !code && httpStatus !== 200)) {
        const status = httpStatus;
        let snippet = "";
        try {
          const raw =
            typeof response?.data === "string"
              ? response.data
              : JSON.stringify(response?.data);
          snippet = (raw || "").replace(/\s+/g, " ").slice(0, 300);
        } catch (_) {}
        console.error("[updateUserProfile] non-JSON response", {
          status,
          data: response?.data,
        });
        return {
          success: false,
          error: `Server error${status ? ` (HTTP ${status})` : ""}${
            snippet ? `: ${snippet}` : ""
          }`,
        };
      }

      return {
        success: false,
        error: result?.MESSAGE || result?.message || `Server returned CODE ${code}`,
        code,
      };
    } catch (error) {
      console.error("[ProfileInfoController] updateUserProfile error:", error);
      return { success: false, error: error.message || "Failed to update profile" };
    }
  }

  /**
   * Normalize documents from the get-profile response shape into the shape the
   * UPDATE_PERSONAL_PROFILE backend expects. The API returns nested files under
   * the lowercase `user_document_file` key, but the update handler reads the
   * uppercase `USER_DOCUMENT_FILES` key (mirrors Flutter's UserDocument.toJson()).
   * Without this, editing an existing document throws
   * "Undefined array key USER_DOCUMENT_FILES" on the server (CODE 707).
   */
  static normalizeDocumentsForUpdate(docs) {
    if (!Array.isArray(docs)) return [];
    return docs.map((doc) => {
      const files =
        doc.USER_DOCUMENT_FILES ||
        doc.user_document_file ||
        doc.user_document_files ||
        doc.USER_DOCUMENT_FILE ||
        doc.userDocumentFiles ||
        doc.userDocumentFile ||
        [];

      return {
        ID: doc.ID ?? doc.id,
        USER_ID: doc.USER_ID ?? doc.userId,
        DOC_TYPE: doc.DOC_TYPE ?? doc.docType ?? doc.doc_type,
        DOC_SUB_TYPE: doc.DOC_SUB_TYPE ?? doc.docSubType ?? doc.doc_sub_type,
        USER_DOCUMENT_FILES: (Array.isArray(files) ? files : []).map((f) => ({
          ID: f.ID ?? f.id,
          DOCUMENT_ID: f.DOCUMENT_ID ?? f.documentId ?? f.document_id,
          FILE_NAME: f.FILE_NAME ?? f.fileName ?? f.file_name,
          FILE_PATH: f.FILE_PATH ?? f.filePath ?? f.file_path,
          IS_DELETED: f.IS_DELETED ?? f.isDeleted ?? f.is_deleted ?? 0,
        })),
      };
    });
  }

  /**
   * Display name used across profile header and sidebar (first + last, else nickname).
   */
  static getDisplayName(userLike = {}) {
    const info = userLike?.user_information || userLike?.userInformation || {};
    const fromParts = [info.FIRST_NAME || info.firstName, info.LAST_NAME || info.lastName]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();

    return (
      fromParts ||
      userLike?.NICK_NAME ||
      userLike?.nickName ||
      info.FULL_NAME ||
      info.fullName ||
      userLike?.USER_NAME ||
      userLike?.userName ||
      "User"
    );
  }

  /**
   * Merge profile edits into session userData and notify listeners (e.g. Sidebar).
   */
  static syncCachedUserProfile({
    nickName,
    firstName,
    lastName,
    userInformation,
  } = {}) {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return null;

      const userData = JSON.parse(raw);
      const info = userData.user_information || userData.userInformation || {};
      if (!userData.user_information) userData.user_information = info;
      if (userData.userInformation) delete userData.userInformation;

      if (nickName !== undefined) {
        userData.NICK_NAME = nickName;
      }
      if (firstName !== undefined) {
        info.FIRST_NAME = firstName;
      }
      if (lastName !== undefined) {
        info.LAST_NAME = lastName;
      }
      if (userInformation && typeof userInformation === "object") {
        Object.assign(info, userInformation);
      }

      const fullName = [info.FIRST_NAME, info.LAST_NAME]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ")
        .trim();
      if (fullName) {
        info.FULL_NAME = fullName;
      }

      sessionStorage.setItem("userData", JSON.stringify(userData));
      sessionStorage.setItem("userDataTimestamp", Date.now().toString());

      try {
        window.dispatchEvent(
          new CustomEvent("mfg:user-profile-updated", {
            detail: { user: userData },
          })
        );
      } catch (_) {}

      return userData;
    } catch (_) {
      return null;
    }
  }

  /**
   * Helper to extract document image URLs from raw profile data.
   */
  static extractDocumentUrls(rawData) {
    const rawDocs =
      rawData?.user_document ||
      rawData?.user_documents ||
      rawData?.USER_DOCUMENTS ||
      rawData?.USER_DOCUMENT ||
      rawData?.userDocuments ||
      [];
    const docs = Array.isArray(rawDocs) ? rawDocs : rawDocs ? [rawDocs] : [];
    const result = {};

    const unwrapEnum = (value) => {
      if (value == null || typeof value !== "object") return value;
      return value.value ?? value.VALUE ?? value.name ?? value.NAME ?? value;
    };

    const numEq = (a, b) => {
      a = unwrapEnum(a);
      b = unwrapEnum(b);
      if (a == null || b == null) return a == null && b == null;
      const aNum = Number(a);
      const bNum = Number(b);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && String(a).trim() !== "" && String(b).trim() !== "") {
        return aNum === bNum;
      }
      return String(a).toUpperCase() === String(b).toUpperCase();
    };

    const asFileList = (files) => {
      if (Array.isArray(files)) return files;
      if (files && typeof files === "object") return [files];
      return [];
    };

    const findDocFile = (docType, docSubType, fileName) => {
      for (const doc of docs) {
        const dt = doc.DOC_TYPE ?? doc.docType ?? doc.doc_type;
        const dst = doc.DOC_SUB_TYPE ?? doc.docSubType ?? doc.doc_sub_type;
        if (numEq(dt, docType) && (docSubType == null || numEq(dst, docSubType))) {
          const files = asFileList(
            doc.user_document_file ||
              doc.user_document_files ||
              doc.USER_DOCUMENT_FILE ||
              doc.USER_DOCUMENT_FILES ||
              doc.userDocumentFiles ||
              doc.userDocumentFile
          );
          for (const f of files) {
            const isDeleted = f.IS_DELETED ?? f.isDeleted ?? f.is_deleted;
            if (isDeleted === true || isDeleted === 1 || isDeleted === "1") continue;
            const fn = f.FILE_NAME ?? f.fileName ?? f.file_name;
            if (fileName != null && String(fn) !== String(fileName)) continue;
            const path = f.FILE_PATH ?? f.filePath ?? f.file_path;
            if (path) return path;
          }
        }
      }
      return null;
    };

    const findWithFallback = (docType, docSubType, fileName) =>
      findDocFile(docType, docSubType, fileName) || findDocFile(docType, docSubType, null);

    result.frontId = buildDocImageUrl(findWithFallback(UserDocumentType.PROOF_OF_IDENTITY, null, DocumentFileName.FRONT_IMAGE));
    result.backId = buildDocImageUrl(findDocFile(UserDocumentType.PROOF_OF_IDENTITY, null, DocumentFileName.BACK_IMAGE));
    result.selfie = buildDocImageUrl(findWithFallback(UserDocumentType.SELFIE, UserDocumentSubType.SELFIE, DocumentFileName.SELFIE));
    result.profile = buildDocImageUrl(
      findWithFallback(UserDocumentType.PROFILE, UserDocumentSubType.PROFILE, DocumentFileName.PROFILE) ||
        findDocFile(UserDocumentType.PROFILE, null, DocumentFileName.PROFILE) ||
        findDocFile(UserDocumentType.PROFILE, null, null)
    );
    result.address = buildDocImageUrl(findDocFile(UserDocumentType.PROOF_OF_ADDRESS, null, null));
    result.signature = buildDocImageUrl(findWithFallback(UserDocumentType.PROOF_OF_SIGNATURE, UserDocumentSubType.SIGNATURE, DocumentFileName.FRONT_IMAGE));

    result.identityDocSubType = null;
    result.addressDocSubType = null;
    for (const doc of docs) {
      const dt = doc.DOC_TYPE ?? doc.docType ?? doc.doc_type;
      const dst = doc.DOC_SUB_TYPE ?? doc.docSubType ?? doc.doc_sub_type;
      const dtNum = Number(dt);
      const dstNum = Number(dst);
      if (dtNum === UserDocumentType.PROOF_OF_IDENTITY && dstNum !== UserDocumentSubType.SELFIE && dstNum !== UserDocumentSubType.PROFILE) {
        result.identityDocSubType = dstNum;
      }
      if (dtNum === UserDocumentType.PROOF_OF_ADDRESS) {
        result.addressDocSubType = dstNum;
      }
    }

    result.rawDocuments = docs;
    return result;
  }

  /**
   * Mark profile as completed.
   */
  static async completeProfile() {
    try {
      const response = await updateProfileStatus();
      const { result, code, isSuccess } = resolveApiOutcome(response);

      if (isSuccess) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || result?.message || `Server returned CODE ${code}`,
        code,
      };
    } catch (error) {
      console.error("[ProfileInfoController] completeProfile error:", error);
      if (
        isBenignServerSideNoise(error?.message) ||
        isBenignServerSideNoise(error?.response?.data)
      ) {
        ProfileInfoController.clearProfileCache();
        return { success: true };
      }
      return { success: false, error: error.message || "Failed to complete profile" };
    }
  }
}

export default ProfileInfoController;
